import React, { useState } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Chart } from 'react-chartjs-2';

import cls from './DoughnutChart.module.scss';
import { useTranslation } from 'react-i18next';
import useStatusesTranslate from '../../../hooks/translate/useStatusesTranslate';

type ChartData = {
  labels: string[];
  dataValues: number[];
  colors: string[];
  borderColors: string[];
};
interface IStatus {
  name: string;
  count: number;
  color: string;
  accepted: boolean;
}

interface IProps {
  totalPercent: number;
  statusesData: Record<string, IStatus>;
  className?: string;
  fontStyle?: string;
  useExternalTooltip?: boolean;
}

ChartJS.register(...registerables);

const DoughnutChart = ({
  totalPercent,
  statusesData,
  className = '',
  fontStyle,
  useExternalTooltip = false,
}: IProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'statistics' });
  const { t: tg } = useTranslation('translation');
  const statusesTypesMap = useStatusesTranslate();

  const statusesArray = Object.values(statusesData);
  const filteredStatuses = statusesArray.filter((status) => status.accepted);
  const otherStatusesData = statusesArray.filter((status) => !status.accepted);

  const calculatePercentage = (count: number, totalOrders: number) =>
    ((count / totalOrders) * 100).toFixed(2);

  const { labels, dataValues, colors } = filteredStatuses.reduce<ChartData>(
    (acc, status) => {
      acc.labels.push(
        statusesTypesMap[status.name] || status.name.replaceAll('_', ' ')
      );
      acc.dataValues.push(status.count);
      acc.colors.push(`${status.color}`);
      acc.borderColors.push(status.color);
      return acc;
    },
    { labels: [], dataValues: [], colors: [], borderColors: [] }
  );

  const otherStatusesOrdersCount = otherStatusesData.reduce(
    (sum, status) => sum + status.count,
    0
  );

  labels.push(t('other-label'));
  dataValues.push(otherStatusesOrdersCount);
  colors.push('#B0B0B033');

  const totalOrders =
    filteredStatuses.reduce((sum, status) => sum + status.count, 0) +
    otherStatusesOrdersCount;

  const data = {
    labels,
    datasets: [
      {
        label: t('statuses-label'),
        data: dataValues,
        backgroundColor: colors,
        hoverBackgroundColor: colors.map(
          (color) => color.replace('33', '66') // Менш прозорий колір при ховері
        ),
        borderRadius: 10,
        spacing: 1,
      },
    ],
  };

  const [tooltipState, setTooltipState] = useState<{
    visible: boolean;
    x: number;
    y: number;
    content: string[];
  }>({
    visible: false,
    x: 0,
    y: 0,
    content: [],
  });

  const handleTooltip = (context: any) => {
    const tooltipModel = context.tooltip;
    if (!tooltipModel || !tooltipModel.opacity) {
      setTooltipState((prev) => ({ ...prev, visible: false }));
      return;
    }

    const tooltipItems = tooltipModel.dataPoints.map((tooltipItem: any) => {
      const index = tooltipItem.dataIndex;
      const label = labels[index];
      const value = tooltipItem.raw;
      const percentage = calculatePercentage(value, totalOrders);

      if (label === t('other-label')) {
        const otherLines = otherStatusesData.map((status) => {
          const otherCount = status.count;
          const otherPercentage = calculatePercentage(otherCount, totalOrders);
          return `- ${statusesTypesMap[status.name] || status.name.replaceAll('_', ' ') || status.name}: ${otherPercentage}% (${otherCount} шт)`;
        });

        return [t('other-label'), ...otherLines];
      }

      return [
        `${t('status-label')} ${statusesTypesMap[label] || label.replaceAll('_', ' ') || label}`,
        `${percentage}% (${value} ${tg('pcs')}) `,
      ];
    });

    setTooltipState({
      visible: true,
      x: tooltipModel.caretX,
      y: tooltipModel.caretY,
      content: tooltipItems.flat(),
    });
  };

  const options = {
    cutout: '80%',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: !useExternalTooltip,
        external: useExternalTooltip
          ? (context: any) => handleTooltip(context)
          : undefined,
      },
    },
  };

  const centerTextPlugin = {
    id: 'centerText',
    beforeDraw: (chart: any) => {
      const { ctx } = chart;
      const { top, bottom, left, right } = chart.chartArea;

      ctx.save();
      const centerX = (left + right) / 2;
      const centerY = (top + bottom) / 2;

      const text = `${totalPercent}%`;
      ctx.font = fontStyle || 'bold 40px Nunito Sans';
      ctx.fillStyle = '#1A1A1A';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, centerX, centerY);

      ctx.restore();
    },
  };

  return (
    <div className={`${cls['chart-container']} ${className}`}>
      {statusesArray?.length > 0 ? (
        <>
          <Chart
            type="doughnut"
            data={data}
            options={options}
            plugins={[centerTextPlugin]}
          />
          {useExternalTooltip && tooltipState.visible && (
            <div
              style={{
                position: 'absolute',
                top: tooltipState.y,
                left: tooltipState.x,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                color: '#fff',
                borderRadius: '6px',
                padding: '8px 12px',
                pointerEvents: 'none',
                zIndex: 1000,
                whiteSpace: 'nowrap',
                fontSize: '12px',
                lineHeight: '1.4',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
              }}
            >
              {tooltipState.content.map((line, index) => (
                <div key={index}>{line}</div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className={`${cls['chart-no-data']}`}>{t('no-data-for-day')}</div>
      )}
    </div>
  );
};

export default DoughnutChart;
